import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  adminGetSettings, adminUpdateSettings,
  adminAdCopyGenerate, adminCampaignBuild,
  adminCapiTest, adminCapiLog,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Megaphone, Activity, Sparkles, Link2, BookOpen, Copy, Check,
  Facebook, Search as SearchIcon, Music2, RefreshCcw, Loader2,
  ExternalLink, Plug,
} from 'lucide-react';

function CopyBtn({ text, label = 'Copied' }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success(label);
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error('Copy failed'); }
  }
  return (
    <Button type="button" size="sm" variant="outline" onClick={copy}
      className="h-8 px-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]">
      {done ? <Check className="h-3.5 w-3.5 text-[hsl(152_78%_55%)]" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function PlatformIcon({ p, className = 'h-4 w-4' }) {
  if (p === 'facebook') return <Facebook className={className} />;
  if (p === 'google') return <SearchIcon className={className} />;
  if (p === 'tiktok') return <Music2 className={className} />;
  return null;
}

const PLATFORM_LABEL = { facebook: 'Facebook', google: 'Google Ads', tiktok: 'TikTok' };

// =================================================================
// 1. PIXELS & CAPI
// =================================================================
function PixelsSection() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => { (async () => {
    try { setS(await adminGetSettings()); }
    catch { toast.error('Failed to load settings'); }
  })(); }, []);

  function up(k, v) { setS({ ...s, [k]: v }); }

  async function save() {
    setSaving(true);
    try {
      await adminUpdateSettings(s);
      toast.success('Pixel & CAPI settings saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  }

  async function ping() {
    setPinging(true);
    try {
      const r = await adminCapiTest();
      setLogs(r.results || []);
      const okCount = (r.results || []).filter((x) => x.sent).length;
      if (okCount > 0) toast.success(`CAPI test: ${okCount} provider(s) OK`);
      else toast.message('CAPI test sent — check provider response below');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CAPI test failed');
    } finally { setPinging(false); }
  }

  if (!s) return <div className="text-sm text-muted-foreground p-6">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="ads-pixels-section">
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plug className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Pixels &amp; Tag IDs
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Paste your tracking pixel IDs. They auto-inject on every public page and fire <code className="font-mono">PageView</code>,
            <code className="font-mono"> Lead</code>, <code className="font-mono">CompleteRegistration</code> and <code className="font-mono">Purchase</code> events.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Facebook Pixel ID" hint="From Meta Events Manager · 15-16 digit number">
              <Input value={s.facebook_pixel_id || ''} onChange={(e) => up('facebook_pixel_id', e.target.value)}
                placeholder="e.g. 1234567890123456" data-testid="ads-fb-pixel" />
            </Field>
            <Field label="TikTok Pixel ID" hint="From TikTok Events Manager · starts with C…">
              <Input value={s.tiktok_pixel_id || ''} onChange={(e) => up('tiktok_pixel_id', e.target.value)}
                placeholder="e.g. C4ABCDEFGHIJKL" data-testid="ads-tt-pixel" />
            </Field>
            <Field label="Google Ads ID" hint="Conversion ID e.g. AW-1234567890">
              <Input value={s.google_ads_id || ''} onChange={(e) => up('google_ads_id', e.target.value)}
                placeholder="AW-1234567890" data-testid="ads-google-ads" />
            </Field>
            <Field label="Google Analytics 4 ID" hint="e.g. G-XXXXXXXXXX">
              <Input value={s.google_analytics_id || ''} onChange={(e) => up('google_analytics_id', e.target.value)}
                placeholder="G-XXXXXXXXXX" data-testid="ads-ga4" />
            </Field>
            <Field label="Google Ads Conversion Label" hint="The /YYY part of `AW-XXX/YYY`. Optional but improves attribution.">
              <Input value={s.google_ads_conversion_label || ''} onChange={(e) => up('google_ads_conversion_label', e.target.value)}
                placeholder="abcDEFghi123" data-testid="ads-google-label" />
            </Field>
          </div>
          <div className="flex items-center justify-end pt-1">
            <Button onClick={save} disabled={saving} className="cta-sheen text-white border-0" data-testid="ads-pixels-save">
              {saving ? 'Saving…' : 'Save Pixels'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* CAPI Tokens */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Conversions API (server-side)
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Optional but RECOMMENDED. iOS 14.5+ blocks ~30% of pixel hits. CAPI sends events from our server directly to Meta/TikTok
            for accurate attribution. Get the access tokens from your platform's Events Manager.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Meta CAPI Access Token" hint="Events Manager → Settings → Generate access token">
              <Input type="password" value={s.facebook_capi_token || ''} onChange={(e) => up('facebook_capi_token', e.target.value)}
                placeholder="EAAxxxxxxxxxxxxxx…" data-testid="ads-fb-capi-token" />
            </Field>
            <Field label="Meta Test Event Code (optional)" hint="For testing in Events Manager → Test Events tab">
              <Input value={s.facebook_capi_test_event_code || ''} onChange={(e) => up('facebook_capi_test_event_code', e.target.value)}
                placeholder="TEST12345" data-testid="ads-fb-capi-test" />
            </Field>
            <Field label="TikTok Events API Token" hint="TikTok Events Manager → Pixel → Set up Events API">
              <Input type="password" value={s.tiktok_capi_token || ''} onChange={(e) => up('tiktok_capi_token', e.target.value)}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxx" data-testid="ads-tt-capi-token" />
            </Field>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <Button size="sm" variant="outline" onClick={ping} disabled={pinging}
              className="border-white/10 bg-white/[0.02] gap-2" data-testid="ads-capi-test">
              {pinging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
              {pinging ? 'Pinging…' : 'Send test event to all CAPIs'}
            </Button>
            <Button onClick={save} disabled={saving} className="cta-sheen text-white border-0" data-testid="ads-capi-save">
              {saving ? 'Saving…' : 'Save Tokens'}
            </Button>
          </div>
          {logs.length > 0 && (
            <div className="rounded-md border border-white/10 bg-black/30 p-2 text-[11px] space-y-1 font-mono">
              {logs.map((r, i) => (
                <div key={i} className={r.sent ? 'text-[hsl(152_78%_55%)]' : 'text-[hsl(0_72%_60%)]'}>
                  [{r.platform}] sent={String(r.sent)} {r.status ? `· ${r.status}` : ''} {r.reason || r.error || ''}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =================================================================
// 2. CAMPAIGN URL BUILDER
// =================================================================
function CampaignBuilderSection() {
  const [platform, setPlatform] = useState('facebook');
  const [campaign, setCampaign] = useState('');
  const [creative, setCreative] = useState('');
  const [destination, setDestination] = useState('');
  const [creating, setCreating] = useState(false);
  const [last, setLast] = useState(null);

  async function build(e) {
    e?.preventDefault();
    if (!campaign.trim()) { toast.error('Campaign name is required'); return; }
    setCreating(true);
    try {
      const r = await adminCampaignBuild({
        platform,
        campaign: campaign.trim(),
        creative: creative.trim() || null,
        destination: destination.trim() || null,
      });
      setLast(r);
      toast.success(`Campaign link created: /s/${r.slug}`);
      try { await navigator.clipboard.writeText(r.short_url); toast.message('Short URL copied'); } catch { /* noop */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to build campaign');
    } finally { setCreating(false); }
  }

  return (
    <div className="space-y-5" data-testid="ads-campaign-section">
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Campaign URL Builder
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Generate a UTM-tagged short link for any platform / campaign / creative — ready to paste into your ad. Every click is
            tracked under <strong>Broker → Analytics → By Platform</strong>.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={build} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Platform</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10" data-testid="ads-camp-platform">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="google">Google Ads</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Campaign Name</Label>
                <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="e.g. jul-launch"
                  className="mt-1 bg-white/[0.02] border-white/10 font-mono text-sm" maxLength={40} data-testid="ads-camp-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Creative (optional)</Label>
                <Input value={creative} onChange={(e) => setCreative(e.target.value)} placeholder="e.g. video1, reel-bd"
                  className="mt-1 bg-white/[0.02] border-white/10 font-mono text-sm" maxLength={40} data-testid="ads-camp-creative" />
              </div>
            </div>
            <Field label="Destination URL (optional)" hint="Default: alveetrade.com home page">
              <Input value={destination} onChange={(e) => setDestination(e.target.value)}
                placeholder="https://alveetrade.com" className="bg-white/[0.02] border-white/10 font-mono text-sm"
                data-testid="ads-camp-dest" />
            </Field>
            <div className="flex items-center justify-end pt-1">
              <Button type="submit" disabled={creating} className="cta-sheen text-white border-0 gap-2" data-testid="ads-camp-build">
                {creating ? 'Building…' : 'Build campaign link'} <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </div>
          </form>

          {last && (
            <div className="mt-4 rounded-md border border-[rgba(245,185,66,0.25)] bg-[rgba(245,185,66,0.04)] p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <PlatformIcon p={last.campaign?.platform} />
                  <code className="font-mono text-sm text-[hsl(43_92%_56%)]">{last.short_url}</code>
                </div>
                <CopyBtn text={last.short_url} label="Short URL copied" />
              </div>
              <div className="text-[11px] text-muted-foreground break-all font-mono">→ {last.url}</div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =================================================================
// 3. AI AD COPY GENERATOR
// =================================================================
const TONES = ['energetic', 'professional', 'playful', 'aggressive', 'educational'];
const LANGS = [
  { v: 'english', l: 'English' },
  { v: 'bangla',  l: 'Bangla / Banglish' },
  { v: 'hindi',   l: 'Hindi' },
];

function AdCopySection() {
  const [platform, setPlatform] = useState('facebook');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('energetic');
  const [language, setLanguage] = useState('english');
  const [angle, setAngle] = useState('');
  const [n, setN] = useState(6);
  const [loading, setLoading] = useState(false);
  const [variations, setVariations] = useState([]);

  async function generate(e) {
    e?.preventDefault();
    setLoading(true);
    setVariations([]);
    try {
      const r = await adminAdCopyGenerate({
        platform, audience: audience.trim() || null, tone, language,
        angle: angle.trim() || null, n,
      });
      setVariations(r.variations || []);
      if (!r.variations?.length) toast.message('Generated empty result; try a different prompt');
      else toast.success(`Generated ${r.variations.length} variation(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed');
    } finally { setLoading(false); }
  }

  function variationToText(v) {
    if (platform === 'facebook') {
      return [
        v.headline && `📢 ${v.headline}`,
        v.primary_text,
        v.description && `— ${v.description}`,
        v.cta && `[CTA: ${v.cta}]`,
      ].filter(Boolean).join('\n');
    }
    if (platform === 'google') {
      return [
        v.headline_1, v.headline_2, v.headline_3,
        '',
        v.description_1, v.description_2,
      ].filter(Boolean).join('\n');
    }
    if (platform === 'tiktok') {
      const tags = Array.isArray(v.hashtags) ? v.hashtags.join(' ') : v.hashtags || '';
      return [v.hook, '', v.caption, tags, v.cta && `[CTA: ${v.cta}]`].filter(Boolean).join('\n');
    }
    return JSON.stringify(v, null, 2);
  }

  return (
    <div className="space-y-5" data-testid="ads-copy-section">
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(43_92%_56%)]" /> AI Ad Copy Generator
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Generates platform-specific ad variations tuned for the trading-education niche. Powered by Claude Sonnet 4.5.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={generate} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Platform</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10" data-testid="ads-copy-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="google">Google Search Ads</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10" data-testid="ads-copy-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="mt-1 bg-white/[0.02] border-white/10" data-testid="ads-copy-lang"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGS.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Field label="Audience description (optional)">
              <Textarea value={audience} onChange={(e) => setAudience(e.target.value)} rows={2}
                placeholder="e.g. 18-35 male, Bangladesh, interested in side income, tired of 9-5"
                className="bg-white/[0.02] border-white/10 text-sm" maxLength={300} data-testid="ads-copy-audience" />
            </Field>
            <Field label="Specific angle / hook (optional)">
              <Input value={angle} onChange={(e) => setAngle(e.target.value)}
                placeholder="e.g. 'turn $5 into $50 with mentor guidance', 'no degree required'"
                className="bg-white/[0.02] border-white/10 text-sm" maxLength={200} data-testid="ads-copy-angle" />
            </Field>
            <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
              <div className="flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Variations:</Label>
                <Input type="number" min={1} max={10} value={n} onChange={(e) => setN(Math.max(1, Math.min(10, Number(e.target.value) || 6)))}
                  className="w-[80px] bg-white/[0.02] border-white/10 h-8 text-sm" />
              </div>
              <Button type="submit" disabled={loading} className="cta-sheen text-white border-0 gap-2" data-testid="ads-copy-generate">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
              </Button>
            </div>
          </form>

          {variations.length > 0 && (
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {variations.map((v, i) => {
                const text = variationToText(v);
                return (
                  <div key={i} className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2"
                    data-testid={`ads-copy-variation-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)]">Variation {i + 1}</span>
                      <CopyBtn text={text} label={`Variation ${i + 1} copied`} />
                    </div>
                    <div className="text-xs leading-relaxed whitespace-pre-line text-[hsl(0_0%_88%)]">{text}</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =================================================================
// 4. CREATIVE TEMPLATE LIBRARY (static curated content)
// =================================================================
const TEMPLATES = {
  facebook: [
    {
      title: 'Hook · Pain → Solution',
      body: '9-5 grind a lagse?\nFREE Telegram community e join koren — daily trading signals + live mentor support.\n[CTA: Join Free Community]',
    },
    {
      title: 'Hook · Social proof',
      body: '131,312+ traders building real income with AlveeTrade. Bangla students $500-$2000/mo earn korche.\nFREE community-tei start.\n[CTA: Sign Up]',
    },
    {
      title: 'Hook · Education-first',
      body: 'Get-rich-quick na — RIGHT NOW shikha shuru koren.\n• Daily live signals\n• Bangla mentor\n• $5 deposit theke trading\n[CTA: Learn More]',
    },
  ],
  google: [
    {
      title: 'Headline trio — Bangla traders',
      body: 'AlveeTrade · Free Trading Course\nDaily Live Signals + Mentor\nStart with $5 · No Experience\nLearn forex, gold, crypto from real traders. Free Telegram community + paid Elite mentorship. Start trading from $5 deposit on Pocket Option (our partner broker).',
    },
    {
      title: 'Headline trio — generic',
      body: 'Trading Mentorship Online\nFree Community · Live Signals\nLearn From 7-Figure Pros\n131K+ students. Daily live calls, real positions, real mentors. Start free, upgrade to Elite when ready.',
    },
  ],
  tiktok: [
    {
      title: 'Hook · POV',
      body: 'Hook: POV: tumi 18, 9-5 jobs er bahire income korte chao 💸\nCaption: Daily signals + Bangla mentor — alveetrade.com e join koro\nHashtags: #trading #bangladesh #sideincome #fyp #forex\n[CTA: Sign Up]',
    },
    {
      title: 'Hook · Number drop',
      body: 'Hook: $5 theke shuru kore 1 month e $200 banaise — kivabe? 👇\nCaption: AlveeTrade community-r real student. Tumi-o try koro — link bio te.\nHashtags: #trading #money #fixedtime #pocketoption #fyp\n[CTA: Learn More]',
    },
  ],
};

const TARGETING_TIPS = {
  facebook: [
    'Age: 18-35, Gender: All',
    'Location: Bangladesh, India, Pakistan, Indonesia, Philippines, Egypt, Brazil, Nigeria',
    'Detailed targeting: Forex / Day trading / Cryptocurrency / Personal finance / Side hustle / Pocket Option / Binance / Robinhood',
    'Behaviors: Engaged shoppers, Tech early adopters',
    'Placement: Reels + Feed (skip Audience Network for trading vertical)',
    'Optimization: Lead → optimize for Lead Form Submission or Link Click',
  ],
  google: [
    'Keywords: "forex trading bangla", "pocket option signal", "binary options bangladesh", "online income bd", "trading course"',
    'Negative keywords: jobs, salary, government, free download',
    'Location: Bangladesh + South Asia tier-1 cities',
    'Bid strategy: Maximize Conversions (after 30 conversions) or Manual CPC',
    'Ad extensions: Sitelinks (Free Community / Elite / Reviews), Callouts ($5 minimum, Daily Signals)',
  ],
  tiktok: [
    'Age: 18-34',
    'Interests: Investing & Cryptocurrency, Stock Trading, Personal Finance, Side hustles, Self-improvement',
    'Location: Bangladesh, India, Pakistan + global English speakers',
    'Optimization goal: Conversion (after 50 events) or Click',
    'Creative: 9:16 vertical, hook in first 1.5s, on-screen captions, native creator-style (no logos for first 3s)',
  ],
};

function TemplatesSection() {
  const [platform, setPlatform] = useState('facebook');
  const items = TEMPLATES[platform] || [];
  const tips = TARGETING_TIPS[platform] || [];
  return (
    <div className="space-y-5" data-testid="ads-templates-section">
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Creative Templates &amp; Targeting
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Battle-tested ad copy starters and audience targeting recommendations for the trading-education niche.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={platform} onValueChange={setPlatform}>
            <TabsList className="bg-white/[0.02]">
              <TabsTrigger value="facebook" className="gap-1.5"><Facebook className="h-3.5 w-3.5" /> Facebook</TabsTrigger>
              <TabsTrigger value="google" className="gap-1.5"><SearchIcon className="h-3.5 w-3.5" /> Google</TabsTrigger>
              <TabsTrigger value="tiktok" className="gap-1.5"><Music2 className="h-3.5 w-3.5" /> TikTok</TabsTrigger>
            </TabsList>
            <TabsContent value={platform} className="space-y-4 mt-3">
              <div className="grid gap-3 md:grid-cols-2">
                {items.map((t, i) => (
                  <div key={i} className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)]">{t.title}</span>
                      <CopyBtn text={t.body} label="Template copied" />
                    </div>
                    <div className="text-xs leading-relaxed whitespace-pre-line text-[hsl(0_0%_88%)]">{t.body}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-md border border-[rgba(245,185,66,0.25)] bg-[rgba(245,185,66,0.04)] p-3">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)] font-semibold mb-2">
                  Recommended targeting · {PLATFORM_LABEL[platform]}
                </div>
                <ul className="text-xs text-[hsl(0_0%_85%)] space-y-1 list-disc pl-4">
                  {tips.map((tip, i) => <li key={i}>{tip}</li>)}
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// =================================================================
// 5. CAPI LOG  (small helper for CAPI tab)
// =================================================================
function CapiLogSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const refresh = useMemo(() => async () => {
    setLoading(true);
    try { const d = await adminCapiLog(30); setItems(d.items || []); }
    catch { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Card className="card-surface" data-testid="ads-capi-log">
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Recent CAPI Forwards
        </CardTitle>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading}
          className="h-8 border-white/10 bg-white/[0.02] gap-1">
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6">No CAPI events forwarded yet.</div>
        ) : (
          <div className="space-y-1 font-mono text-[11px] max-h-[280px] overflow-y-auto">
            {items.map((r, i) => (
              <div key={i} className="rounded-sm border border-white/5 bg-black/20 px-2 py-1">
                <span className="text-[hsl(43_92%_56%)]">{r.event_name}</span>
                <span className="text-muted-foreground"> · {r.country_code || '—'} · {r.click_id?.slice(0, 12) || 'no-click'}</span>
                {(r.results || []).map((x, j) => (
                  <span key={j} className={`ml-1 ${x.sent ? 'text-[hsl(152_78%_55%)]' : 'text-[hsl(0_72%_60%)]'}`}>
                    [{x.platform || '?'}:{x.sent ? 'OK' : 'FAIL'}{x.status ? ` ${x.status}` : ''}]
                  </span>
                ))}
                <span className="text-muted-foreground/60 ml-1">{(r.created_at || '').slice(11, 19)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =================================================================
// MAIN TAB
// =================================================================
export default function AdminAdsTab() {
  const [section, setSection] = useState('pixels');
  return (
    <div data-testid="admin-ads-tab" className="space-y-4">
      <Card className="card-surface">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Megaphone className="h-4 w-4 text-[hsl(43_92%_56%)]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mr-1">Section</span>
            <SectionPill active={section === 'pixels'} onClick={() => setSection('pixels')} testid="ads-section-pixels">
              <Plug className="h-3.5 w-3.5" /> Pixels &amp; CAPI
            </SectionPill>
            <SectionPill active={section === 'campaigns'} onClick={() => setSection('campaigns')} testid="ads-section-campaigns">
              <Link2 className="h-3.5 w-3.5" /> Campaign Builder
            </SectionPill>
            <SectionPill active={section === 'ai'} onClick={() => setSection('ai')} testid="ads-section-ai">
              <Sparkles className="h-3.5 w-3.5" /> AI Ad Copy
            </SectionPill>
            <SectionPill active={section === 'templates'} onClick={() => setSection('templates')} testid="ads-section-templates">
              <BookOpen className="h-3.5 w-3.5" /> Templates &amp; Targeting
            </SectionPill>
            <SectionPill active={section === 'log'} onClick={() => setSection('log')} testid="ads-section-log">
              <Activity className="h-3.5 w-3.5" /> CAPI Log
            </SectionPill>
          </div>
        </CardContent>
      </Card>

      {section === 'pixels' && <PixelsSection />}
      {section === 'campaigns' && <CampaignBuilderSection />}
      {section === 'ai' && <AdCopySection />}
      {section === 'templates' && <TemplatesSection />}
      {section === 'log' && <CapiLogSection />}
    </div>
  );
}

function SectionPill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
        active
          ? 'bg-[hsl(43_92%_56%)] text-black border border-transparent'
          : 'border border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
